import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButton, IonList, IonItem, IonLabel, IonGrid, IonRow, IonCol, IonInput, IonModal, IonPicker, IonCard, IonBadge, IonSpinner } from '@ionic/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import './SelectMasterPlot.css';
import { getFarmIdsOfUser, getPlotIdsOfFarm } from '../services/farm.service';
import UserList from '../pages/UserList';
import { updatePlotDetails, getNearestPlots } from '../services/plot.service';

interface IProps {
    plot: any;
    setMasterPlot: Function;
    setShowMasterPlotModal: Function;
    location: { lat: string; lng: string } | null;
}

const FarmPicker = {
    name: "FarmId",
    options: [] as any[]
};

const setFarmPicker = (farmIds: any[]) => {
    FarmPicker.options = [];
    farmIds.forEach(item => {
        FarmPicker.options.push({
            text: item.farmId,
            value: item.farmId
        })
    });
};

const PlotPicker = {
    name: "PlotId",
    options: [] as any[]
};

const setPlotPicker = (plotIds: any[]) => {
    PlotPicker.options = [];
    plotIds.forEach(item => {
        PlotPicker.options.push({
            text: item.plotId,
            value: item.plotId
        })
    });
};

const NearestPlotCard: React.FC<{ plot: any; onSelect: (plotId: string) => void }> = ({ plot, onSelect }) => {
    const distKm = plot.distanceInMeters != null
        ? (plot.distanceInMeters / 1000).toFixed(2) + ' km'
        : '';
    return (
        <IonCard onClick={() => onSelect(plot.plotId)} style={{ margin: '8px', cursor: 'pointer' }}>
            <IonItem lines="none" style={{ '--background': 'transparent' } as any}>
                <IonLabel>
                    <h3 style={{ fontWeight: 'bold' }}>{plot.name || plot.plotId}</h3>
                    {plot.farmerName && <p>{plot.farmerName}</p>}
                    {plot.farmName && <p style={{ color: '#666' }}>{plot.farmName}</p>}
                    <p style={{ fontSize: '12px', color: '#999' }}>ID: {plot.plotId}</p>
                </IonLabel>
                <div slot="end" style={{ textAlign: 'right' }}>
                    {distKm && <div style={{ fontSize: '13px', color: '#3880ff' }}>{distKm} away</div>}
                    {plot.deviceType && <IonBadge color="primary" style={{ fontSize: '11px' }}>{plot.deviceType}</IonBadge>}
                </div>
            </IonItem>
        </IonCard>
    );
};


const SelectMasterPlot: React.FC<IProps> = (props) => {
    const formik = useFormik({
        initialValues: {
            farmUserId: '',
            farmId: '',
            plotId: ''
        },
        validationSchema: Yup.object({
            farmUserId: Yup.string().trim().required('Required'),
            farmId: Yup.string().trim().required('Required'),
            plotId: Yup.string().trim().required('Required')
        }),
        onSubmit: values => {
            if (props?.plot?.plotId) {
                updatePlotDetails(props.plot.plotId, {
                    parentId: values.plotId,
                    hiddenSensors: [
                        'soilTemp',
                        'soilMoisture1',
                        'soilMoisture2'
                    ],
                });
            }
            props.setMasterPlot(values.plotId);
        },
    });

    const [showSelectUserModal, setShowSelectUserModal] = useState(false);
    const [nearestPlots, setNearestPlots] = useState<any[]>([]);
    const [loadingNearest, setLoadingNearest] = useState(false);
    const [nearestError, setNearestError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);

    function setFarmUserId(item: any) {
        formik.setFieldValue('farmUserId', item.farmUserId);
        formik.setFieldValue('farmId', '');
        formik.setFieldValue('plotId', '');
        setShowSelectUserModal(false);
    }

    const [farmPickerIsOpen, setFarmPickerIsOpen] = useState(false);
    useEffect(() => {
        let mounted = true;
        if (formik.values.farmUserId) {
            getFarmIdsOfUser(formik.values.farmUserId)
                .then(items => {
                    if (mounted) {
                        setFarmPicker(items)
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.farmUserId])

    const [plotPickerIsOpen, setPlotPickerIsOpen] = useState(false);
    useEffect(() => {
        let mounted = true;
        if (formik.values.farmId) {
            getPlotIdsOfFarm(formik.values.farmId)
                .then(items => {
                    if (mounted) {
                        setPlotPicker(items)
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.farmId])

    async function fetchNearestPlots() {
        const lat = parseFloat(props.location?.lat || '');
        const lng = parseFloat(props.location?.lng || '');
        if (!lat || !lng) {
            setNearestError('Location not set. Fill in lat/lng on the form first.');
            return;
        }
        setNearestError(null);
        setLoadingNearest(true);
        try {
            const results = await getNearestPlots(
                { latitude: lat, longitude: lng },
                {
                    deviceType: {
                        $in: [
                            'WEATHER_UNIT',
                            'MASTER_UNIT',
                            'KAIRO_UNIT',
                            'KAIRO_DOUBLE_UNIT',
                            'ZUNO_UNIT',
                            'MYKO_UNIT',
                            'KAIRO_INFINITY_UNIT'
                        ]
                    }
                },
                5,
            );
            if (!Array.isArray(results)) throw new Error(results?.error || 'Unknown error');
            const filtered = results.filter((p: any) => p.distanceInMeters <= 10000);
            setNearestPlots(filtered);
        } catch (err: any) {
            setNearestError('Failed to fetch nearest plots.');
        } finally {
            setLoadingNearest(false);
            setHasSearched(true);
        }
    }

    function onClose() {
        props.setMasterPlot('');
        props.setShowMasterPlotModal(false);
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Select Master Plot</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                <IonGrid>
                    <IonRow>
                        <IonCol>
                            {(!props.location?.lat || !props.location?.lng) ? (
                                <div style={{ padding: '12px', color: '#999', fontSize: '14px' }}>
                                    Location not set — fill in lat/lng to use nearest plots.
                                </div>
                            ) : (
                                <IonButton expand="block" onClick={fetchNearestPlots} disabled={loadingNearest}>
                                    {loadingNearest ? <IonSpinner name="dots" /> : 'Show Nearest Weather Plots'}
                                </IonButton>
                            )}
                        </IonCol>
                    </IonRow>
                </IonGrid>

                {nearestError && (
                    <div style={{ padding: '8px 16px', color: 'red', fontSize: '13px' }}>{nearestError}</div>
                )}

                {nearestPlots.length > 0 && (
                    <>
                        <IonItem lines="none">
                            <IonLabel style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                Nearest Weather Plots ({nearestPlots.length})
                            </IonLabel>
                        </IonItem>
                        {nearestPlots.map((p: any) => (
                            <NearestPlotCard
                                key={p.plotId}
                                plot={p}
                                onSelect={(plotId: string) => {
                                    if (props?.plot?.plotId) {
                                        updatePlotDetails(props.plot.plotId, {
                                            parentId: plotId,
                                            hiddenSensors: ['soilTemp', 'soilMoisture1', 'soilMoisture2'],
                                        });
                                    }
                                    props.setMasterPlot(plotId);
                                }}
                            />
                        ))}
                    </>
                )}

                {hasSearched && nearestPlots.length === 0 && !nearestError && (
                    <div style={{ padding: '8px 16px', color: '#999', fontSize: '13px' }}>
                        No weather plots found within 10 km.
                    </div>
                )}

                <IonItem lines="full">
                    <IonLabel style={{ fontWeight: 'bold', fontSize: '14px' }}>Or select manually</IonLabel>
                </IonItem>

                <form onSubmit={formik.handleSubmit}>
                    <IonList>
                        <IonItem>
                            <IonLabel position="stacked">User Id</IonLabel>
                            <IonInput readonly id="farmUserId" name="farmUserId" value={formik.values.farmUserId} placeholder="Select User Id" onClick={e => setShowSelectUserModal(true)}></IonInput>
                        </IonItem>
                        {formik.touched.farmUserId && formik.errors.farmUserId ? (<div className="errorMsg">{formik.errors.farmUserId}</div>) : null}

                        <IonItem>
                            <IonLabel position="stacked">Farm Id</IonLabel>
                            <IonInput readonly disabled={!formik.values.farmUserId} id="farmId" name="farmId" value={formik.values.farmId} placeholder="Select Farm Id" onClick={e => setFarmPickerIsOpen(true)}></IonInput>
                        </IonItem>
                        {formik.touched.farmId && formik.errors.farmId ? (<div className="errorMsg">{formik.errors.farmId}</div>) : null}

                        <IonItem>
                            <IonLabel position="stacked">Plot Id</IonLabel>
                            <IonInput readonly disabled={!formik.values.farmId} id="plotId" name="plotId" value={formik.values.plotId} placeholder="Plot Id" onClick={e => setPlotPickerIsOpen(true)}></IonInput>
                        </IonItem>
                        {formik.touched.plotId && formik.errors.plotId ? (<div className="errorMsg">{formik.errors.plotId}</div>) : null}

                    </IonList>
                    <IonGrid>
                        <IonRow>
                            <IonCol><IonButton className="shiftRight" type="submit">Select</IonButton></IonCol>
                            <IonCol><IonButton onClick={onClose}>Close</IonButton></IonCol>
                        </IonRow>
                    </IonGrid>

                </form>

                {showSelectUserModal && <IonModal isOpen={showSelectUserModal} >
                <UserList
                    selectedValue={formik.values.farmUserId}
                    onClickHandler={setFarmUserId}
                    onClose={setShowSelectUserModal}
                />
                </IonModal>}

                <IonPicker
                    isOpen={farmPickerIsOpen}
                    columns={[FarmPicker]}
                    buttons={[
                        {
                            text: "Cancel",
                            role: "cancel",
                            handler: value => {
                                setFarmPickerIsOpen(false);
                            }
                        },
                        {
                            text: "Confirm",
                            handler: value => {
                                formik.values.farmId = value.FarmId.value;
                                formik.values.plotId = '';
                                setFarmPickerIsOpen(false);
                            }
                        }
                    ]}
                ></IonPicker>

                <IonPicker
                    isOpen={plotPickerIsOpen}
                    columns={[PlotPicker]}
                    buttons={[
                        {
                            text: "Cancel",
                            role: "cancel",
                            handler: value => {
                                setPlotPickerIsOpen(false);
                            }
                        },
                        {
                            text: "Confirm",
                            handler: value => {
                                formik.values.plotId = value.PlotId.value;
                                setPlotPickerIsOpen(false);
                            }
                        }
                    ]}
                ></IonPicker>
            </IonContent>
        </IonPage>
    );
};

export default SelectMasterPlot;
